import type {
  LinkedTaskSummaryItemStatus,
  QueuedReason,
  TaskView,
  WorkflowStepType,
  WorkflowView,
} from '@/lib/api/generated/model';
import { buildBoard, type BoardStepSection } from './boardModel';
import { deriveCardExecutionState, latestExecution } from './executionStatus';
import { runsOnCurrentStep, stepEntryTime } from './executionHistory';
import { locateTask } from './locateTask';
import { notStartedCount } from './linkedChildren';
import {
  buildSubtaskParentLookup,
  resolveSubtaskParent,
  type SubtaskParentRef,
} from './subtaskParent';

/**
 * What a whole workspace's tasks add up to, read the way a board card reads
 * one.
 *
 * A view's reading of the aggregate, recomputed on every render and stored
 * nowhere — the same thing `CardExecutionState` is, one workspace wider.
 * Deliberately not named for any contract entity: nothing here is a
 * `TaskView`, and a name suggesting otherwise would invite someone to send it
 * somewhere.
 *
 * The whole page comes out of one walk. That is not a performance choice: the
 * counters and the lists are derived in the same pass so they cannot disagree,
 * and a counter reading "3 failed" above a list showing two is exactly the bug
 * a second pass would eventually produce.
 */
export interface WorkspaceTaskSummary {
  /**
   * Uncapped, both groups. The cap is five rows, and it belongs to the
   * component: a derivation that truncated could not also report how much it
   * had left out, and saying so is what makes the cap honest.
   */
  attention: {
    needsAttention: AttentionRow[];
    waiting: AttentionRow[];
    /** Parents whose subtasks wait to be started (spec 152). */
    toStart: AttentionRow[];
  };
  /**
   * The one place most of the broken tasks are, when there is one.
   *
   * `null` unless a strict majority of the broken group shares a board and a
   * step. That is a definition of "most", not a judgement about health — the
   * distinction matters, because a threshold asserting a task is *stuck* is
   * exactly what this feature refused to invent, and this asserts nothing
   * except that four rows out of five say the same thing.
   */
  concentration: AttentionConcentration | null;
  counts: WorkspaceTaskCounts;
  /**
   * The same tasks `counts.running` and `counts.waitingOnSubtasks` count — one
   * population, listed once, each row saying which of the two it is.
   */
  active: ActiveRow[];
  /**
   * Waiting for a slot (spec 116 FR-003), in place order — unplaced last, then by
   * title. Counted at rest, never as running (FR-006): nothing is running yet.
   */
  queued: QueuedRow[];
  /**
   * One row per published workflow whose board built, heaviest first.
   *
   * The same walk that fills `counts`, grouped by workflow instead of totalled
   * — not a second pass. A board row and the counters are therefore two
   * summations of one traversal, which is the only way they cannot disagree.
   */
  boards: BoardLoad[];
  unreadableWorkflows: UnreadableWorkflow[];
}

export interface WorkspaceTaskCounts {
  /**
   * An agent run executing: what the Runs meter counts. A parent waiting on its
   * subtasks holds no slot, so it is not here (spec 150 FR-002).
   */
  running: number;
  /** Parents whose run waits on the subtasks it created (spec 150). */
  waitingOnSubtasks: number;
  /**
   * Everything in the Needs-attention group: failed runs, failed workdirs,
   * tasks off the board, stopped runs and runs a spend limit refused. The
   * overview's counter; `failed` below stays the narrower figure.
   */
  needsAttention: number;
  waiting: number;
  /**
   * Failed *runs* only.
   *
   * A broken workdir and an orphaned position are both in the broken group and
   * neither is counted here. The three alarm counters keep the board header's
   * words, and those words have to keep the board's meanings — inflating
   * "failed" with two other kinds of broken is how Overview and a board would
   * come to disagree about one workspace.
   */
  failed: number;
  /**
   * Placed on a board and blocked on nothing — not started, completed, or idle.
   *
   * Counted in the same switch as the other three rather than subtracted from
   * `tasks` by a caller, because the subtraction is only correct while every
   * other branch is accounted for, and a fourth kind of blocked task added
   * later would silently be reported here as resting. Incrementing it where the
   * decision is actually made means a new branch has to choose.
   */
  atRest: number;
  /**
   * Every task the page could place: the context figure that tells a quiet
   * workspace from an empty one. Tasks of a workflow whose board would not
   * build are not among them (see `unreadableWorkflows`).
   */
  tasks: number;
}

/**
 * What one board is carrying, as the four states a reader sorts boards by.
 *
 * `broken` is the Needs-attention group's population, not `counts.failed`: the
 * question this row answers is "which board do I open first", and a workflow
 * holding three orphaned tasks needs opening exactly as much as one holding
 * three failed runs. The row says "broken" for that reason — the page's own
 * word for those three kinds together, so the wider count cannot be mistaken
 * for the narrower counter above it.
 *
 * The four are disjoint and sum to `total`, which is what lets a caller draw
 * them as one bar without normalising anything.
 */
export interface BoardLoad {
  workflowKey: string;
  workflowName: string;
  broken: number;
  waiting: number;
  running: number;
  waitingOnSubtasks: number;
  atRest: number;
  /** The tasks this board holds. The sum of the five above. */
  total: number;
  /**
   * Where those tasks are standing, in board order.
   *
   * The board's own columns, counted — so "everything is piled on one step" is
   * answerable without opening the board. Orphans are included at the start
   * step, because that is where `buildBoard` places them; they are named
   * separately in the broken group, which is where that fact belongs.
   */
  steps: BoardStepLoad[];
}

/** One step of a board, and how many tasks are standing on it. */
export interface BoardStepLoad {
  stepId: string;
  label: string;
  count: number;
}

/**
 * "Most of what is broken is in one place."
 *
 * Derived by grouping the broken rows the walk already produced — no second
 * pass over tasks, and nothing read that is not already on a row.
 */
export interface AttentionConcentration {
  workflowKey: string;
  workflowName: string;
  /** `null` when the shared position is "no step on this board". */
  stepLabel: string | null;
  /** Rows in this group. Always a strict majority of `total`, and at least two. */
  count: number;
  /** The whole broken group, so the component can say "4 of 5". */
  total: number;
  /** True when every row in the group carries the same failure message. */
  sameFailure: boolean;
  /** The board this group is on, so the callout can offer it. */
  hasBoard: boolean;
}

/**
 * Why a task is in Needs attention. Decides its group and where its row links.
 *
 * `human-gate` and `branching` are separate although both are a person being
 * waited on, because the row says which — a board card cannot spare the words
 * and a row can.
 */
export type AttentionKind =
  | 'failed-run'
  | 'failed-workdir'
  | 'orphaned'
  | 'human-gate'
  | 'branching'
  /**
   * Stopped before dispatch because the workspace reached its spend limit
   * (spec 072 FR-026, FR-027).
   *
   * In Needs attention since spec 150, where it used to be in the waiting group.
   * The waiting group is "Waiting on you", and a person reading it cannot release a
   * spend limit; the board's own badge already calls a blocked run "Needs
   * attention" (the service's `needs_attention`: failed, blocked or stopped). Its
   * row still says what it is — "Stopped by spend limit" — and wears the waiting
   * colour the board gives it, so it is never read as a crash.
   */
  | 'spend-limit'
  | 'needs-account'
  /**
   * The latest run was stopped by a person (spec 150).
   *
   * It used to be counted at rest: nobody waits on a run someone chose to end. That
   * stops holding once the task is a subtask — a stopped review holds its release
   * indefinitely, and the overview said "0 Broken" while it did. The board marks
   * every stopped task "Needs attention", and the overview now agrees with it.
   */
  | 'stopped'
  /**
   * A parent whose subtasks wait for a person to start them (spec 152 US1):
   * its run created them with `start_children: manual`. Listed in the person's
   * queue, while the parent is still counted as waiting on its subtasks.
   */
  | 'subtasks-to-start';

export interface AttentionRow {
  taskId: string;
  title: string;
  /** For the link. Overview spans workflows, so a row naming only a task is unlocatable. */
  workflowKey: string;
  /** For the reader. */
  workflowName: string;
  /** `null` only for an orphan: there is no step to name. */
  stepLabel: string | null;
  /**
   * What kind of step that is — an agent's or a person's.
   *
   * `null` for an orphan, which stands on no step at all. Carried so a row can
   * mark the step the way the workflow graph does rather than leaving the
   * reader to infer from the name whether a machine or a human is holding it.
   */
  stepKind: WorkflowStepType | null;
  /** When the task arrived where it stands. Formatted by the component, not here. */
  enteredAt: string;
  /** Runs of the current step. The component shows it only above one. */
  attempts: number;
  kind: AttentionKind;
  /** Present only for `failed-run`, already fallen back to `NO_FAILURE_DETAIL`. */
  failureMessage?: string;
  /**
   * False when the task's workflow is not published, so the row renders
   * unlinked rather than pointing at a board that does not exist.
   */
  hasBoard: boolean;
  /**
   * The task itself, the step it stands on and its board's steps, so a row
   * waiting on a person can offer the decision inline (spec 125 FR-006) with
   * the same control the drawer uses — and refuse a busy task, and send the
   * right version, without a second read.
   */
  task: TaskView;
  step: BoardStepSection | undefined;
  steps: readonly BoardStepSection[];
  /** Whose subtask this is, when it is one (spec 150 FR-005). */
  parent?: SubtaskParentRef;
  /** For `subtasks-to-start`: how many subtasks wait to be started. */
  toStart?: number;
}

/**
 * The two states an active row can be in (spec 150).
 *
 * `waiting-on-subtasks` is a parent whose run waits on the tasks it created: the
 * card's `live` with `children`. It holds no capacity slot and nothing of its own
 * is executing, so it must never read as running.
 */
export type ActiveRowState = 'running' | 'waiting-on-subtasks';

export interface ActiveRow {
  taskId: string;
  title: string;
  workflowKey: string;
  workflowName: string;
  stepLabel: string | null;
  /**
   * What kind of step that is — an agent's or a person's.
   *
   * `null` for an orphan, which stands on no step at all. Carried so a row can
   * mark the step the way the workflow graph does rather than leaving the
   * reader to infer from the name whether a machine or a human is holding it.
   */
  stepKind: WorkflowStepType | null;
  /**
   * `null` when the run has not recorded one. The component then omits the
   * duration rather than printing a placeholder: an em-dash in a duration slot
   * reads as "measured, and the answer was nothing".
   */
  startedAt: string | null;
  hasBoard: boolean;
  state: ActiveRowState;
  /** For `waiting-on-subtasks`: "Waiting on 3 of 4 · Design review". */
  children?: { open: number; total: number; workflowName: string | null };
  /**
   * For `waiting-on-subtasks`: one entry per subtask in the batch, in the order
   * the board card's bar draws them.
   */
  segments?: LinkedTaskSummaryItemStatus[];
  parent?: SubtaskParentRef;
}

/**
 * The order a parent's bar draws its subtasks in, the board card's
 * (`TaskCard`'s `LinkedTaskCounter`): finished work first, unstarted last.
 */
const SEGMENT_ORDER: readonly LinkedTaskSummaryItemStatus[] = [
  'done',
  'needs_attention',
  'needs_review',
  'in_progress',
  'not_started',
];

/** A task whose latest run waits for a slot (spec 116). */
export interface QueuedRow {
  taskId: string;
  title: string;
  workflowKey: string;
  workflowName: string;
  stepLabel: string | null;
  stepKind: WorkflowStepType | null;
  hasBoard: boolean;
  /** 1-based place in the workspace's line; `null` from an older service. */
  position: number | null;
  reason: QueuedReason | null;
  /**
   * When it started waiting (spec 118): the queued run's request time. A run is
   * placed in line the moment it is requested, so the two are the same moment.
   */
  queuedAt: string | null;
  parent?: SubtaskParentRef;
}

/** A workflow whose board `buildBoard` refuses. Its tasks are counted nowhere. */
export interface UnreadableWorkflow {
  workflowKey: string;
  workflowName: string;
  /** A draft is still being drawn; its missing board is not a fault. */
  isDraft: boolean;
}

/**
 * Read a whole workspace the way the board reads one task.
 *
 * Pure: no clock, no formatting, no addresses. Timestamps come out as the
 * service wrote them and counts as integers, because a caller that wanted a
 * formatted span would be a caller this function had to be re-run for.
 *
 * One board is built per workflow. That is more work than reading `position`
 * directly, and it is the point: the alternative is a second implementation of
 * "what state is this task in", which would drift from the board's the first
 * time either changed.
 */
export function summarizeWorkspaceTasks(
  workflows: WorkflowView[],
  tasks: TaskView[],
  /**
   * Every task a row may name as a parent. Defaults to `tasks`; the overview
   * passes the unfiltered list, so a search that leaves a subtask on screen
   * still names the release it belongs to.
   */
  parentCandidates: readonly TaskView[] = tasks,
): WorkspaceTaskSummary {
  const parents = buildSubtaskParentLookup(workflows, parentCandidates);
  const parentOf = (task: TaskView) =>
    resolveSubtaskParent(task, parents) ?? undefined;
  const broken: AttentionRow[] = [];
  const waiting: AttentionRow[] = [];
  const toStart: AttentionRow[] = [];
  const active: ActiveRow[] = [];
  const queued: QueuedRow[] = [];
  const boards: BoardLoad[] = [];
  const unreadableWorkflows: UnreadableWorkflow[] = [];
  const counts: WorkspaceTaskCounts = {
    running: 0,
    waitingOnSubtasks: 0,
    needsAttention: 0,
    waiting: 0,
    failed: 0,
    atRest: 0,
    tasks: 0,
  };

  for (const workflow of workflows) {
    const held = tasks.filter((task) => task.workflow_id === workflow.id);
    const result = buildBoard(workflow, held);

    // A workflow whose columns cannot be derived says nothing about its tasks,
    // so this reports the workflow and abandons them rather than reading them
    // against a board that does not exist. Counting them as healthy would be
    // the worst thing this function could return.
    if (!result.ok) {
      unreadableWorkflows.push({
        workflowKey: workflow.key,
        workflowName: workflow.name,
        isDraft: workflow.status === 'draft',
      });
      continue;
    }

    const { board } = result;
    // A draft has no board, so a row for one of its tasks can link nowhere.
    const hasBoard = workflow.status === 'published';

    // Filled alongside the totals below. Pushed only for a published workflow:
    // a board row is an invitation to open a board, and a draft has none.
    const load: BoardLoad = {
      workflowKey: workflow.key,
      workflowName: workflow.name,
      broken: 0,
      waiting: 0,
      running: 0,
      waitingOnSubtasks: 0,
      atRest: 0,
      total: 0,
      // Read straight off the board that was just built, rather than counted
      // again from `held`: the columns are the board's own answer to "where is
      // everything", and recomputing it here is how the strip and the board it
      // previews would come to disagree.
      steps: board.columns.flatMap((column) =>
        column.steps.map((step) => ({
          stepId: step.stepId,
          label: step.label,
          count: step.tasks.length,
        })),
      ),
    };
    if (hasBoard) boards.push(load);
    const boardSteps = board.columns.flatMap((column) => column.steps);

    for (const task of held) {
      counts.tasks += 1;
      load.total += 1;

      const location = locateTask(board, task.id);
      const parent = parentOf(task);
      const base = {
        taskId: task.id,
        title: task.title,
        workflowKey: workflow.key,
        workflowName: workflow.name,
        stepLabel: location?.stepLabel ?? null,
        stepKind: location?.step.kind ?? null,
        enteredAt: stepEntryTime(task),
        attempts: runsOnCurrentStep(task),
        hasBoard,
        task,
        step: location?.step,
        steps: boardSteps,
        ...(parent ? { parent } : {}),
      };

      // The two structural refusals are read before the card state, because
      // both mean the task cannot proceed at all — where every card state
      // describes a task that is somewhere legitimate. `locateTask` returns
      // `undefined` for an orphan by design, so the card derivation would
      // otherwise describe it as merely at rest.
      if (board.orphanTaskIds.has(task.id)) {
        broken.push({ ...base, kind: 'orphaned' });
        load.broken += 1;
        continue;
      }

      if (task.workdir?.status === 'failed') {
        broken.push({ ...base, kind: 'failed-workdir' });
        load.broken += 1;
        continue;
      }

      const state = deriveCardExecutionState(task, location?.step);

      switch (state.kind) {
        case 'live': {
          const row: ActiveRow = {
            taskId: task.id,
            title: task.title,
            workflowKey: workflow.key,
            workflowName: workflow.name,
            stepLabel: base.stepLabel,
            stepKind: base.stepKind,
            startedAt:
              latestExecution(task.executions ?? [])?.started_at ?? null,
            hasBoard,
            state: 'running',
            ...(parent ? { parent } : {}),
          };
          if (state.children) {
            // Some of its subtasks wait for a person to start them: the
            // person's turn as well (spec 152). It stays counted below.
            const unstarted = notStartedCount(task.linked_tasks);
            if (unstarted > 0) {
              toStart.push({
                ...base,
                kind: 'subtasks-to-start',
                toStart: unstarted,
              });
            }
            // Waiting on the subtasks its run created: no slot held, nothing of
            // its own executing (spec 150 FR-003).
            counts.waitingOnSubtasks += 1;
            load.waitingOnSubtasks += 1;
            active.push({
              ...row,
              state: 'waiting-on-subtasks',
              children: {
                open: state.children.open,
                total: state.children.total,
                workflowName: state.children.workflowName ?? null,
              },
              segments: segmentsOf(task),
            });
          } else {
            counts.running += 1;
            load.running += 1;
            active.push(row);
          }
          break;
        }

        case 'failed':
          counts.failed += 1;
          load.broken += 1;
          broken.push({
            ...base,
            kind: 'failed-run',
            failureMessage: state.message,
          });
          break;

        case 'blocked':
          // Needs attention, where the board badges a blocked run. The row's word
          // is "Stopped by spend limit" and its colour the waiting one, so it is
          // never read as a crash: nothing went wrong with the work, and an
          // administrator releases it, not the reader.
          load.broken += 1;
          broken.push({ ...base, kind: 'spend-limit' });
          break;

        case 'needs-account':
          // Needs attention like a spend block, waiting on a person too — but its
          // person links an account; nobody raises a limit (spec 160).
          load.broken += 1;
          broken.push({ ...base, kind: 'needs-account' });
          break;

        case 'stopped':
          // Needs attention since spec 150 — see `AttentionKind`'s note above.
          load.broken += 1;
          broken.push({ ...base, kind: 'stopped' });
          break;

        case 'waiting-human':
          counts.waiting += 1;
          load.waiting += 1;
          waiting.push({
            ...base,
            kind: state.reason === 'human-gate' ? 'human-gate' : 'branching',
          });
          break;

        case 'queued':
          // At rest for the counts — nothing is running — and listed on its own
          // so Active now can say it waits (spec 116 FR-003, FR-006).
          counts.atRest += 1;
          load.atRest += 1;
          queued.push({
            taskId: task.id,
            title: task.title,
            workflowKey: workflow.key,
            workflowName: workflow.name,
            stepLabel: base.stepLabel,
            stepKind: base.stepKind,
            hasBoard,
            position: state.position,
            reason: state.reason,
            queuedAt:
              task.executions?.find((run) => run.status === 'queued')
                ?.created_at ?? null,
            ...(parent ? { parent } : {}),
          });
          break;

        // `not-started`, `completed` and `idle` are the states nothing is
        // blocked on. They are counted and drawn as the resting remainder, and
        // listed nowhere: a page that surfaced them would be a task list, not an
        // answer to "is anything wrong". (`stopped` was here until spec 150.)
        default:
          counts.atRest += 1;
          load.atRest += 1;
          break;
      }
    }
  }

  // Longest-standing first, in both groups.
  //
  // Sorting is not the stall detection this feature refused: it asserts only
  // that seven days is longer than one, which needs no invented number. What it
  // buys is that each group becomes a queue rather than a set — the walk's own
  // order is workflow-by-workflow, which put a task that broke an hour ago
  // above one that broke a week ago for no reason a reader could see.
  broken.sort(compareDwell);
  waiting.sort(compareDwell);
  toStart.sort(compareDwell);
  queued.sort(compareQueued);
  counts.needsAttention = broken.length;

  return {
    attention: { needsAttention: broken, waiting, toStart },
    concentration: concentrationOf(broken),
    counts,
    active,
    queued,
    boards: boards.sort(compareBoardLoad),
    unreadableWorkflows,
  };
}

/** Longest here first. Rows whose entry time will not parse sort last, together. */
function compareDwell(a: AttentionRow, b: AttentionRow): number {
  const at = Date.parse(a.enteredAt);
  const bt = Date.parse(b.enteredAt);
  const aBad = Number.isNaN(at);
  const bBad = Number.isNaN(bt);
  if (aBad || bBad) return Number(aBad) - Number(bBad);
  return at - bt;
}

/**
 * The largest group of broken rows sharing a board and a step, when it is a
 * strict majority of them.
 *
 * Two rows out of four is not "most", so the bar is `count * 2 > total` rather
 * than `>=`: a callout that fires on half the list would appear on almost every
 * workspace holding two failures and would stop being read.
 */
function concentrationOf(
  broken: AttentionRow[],
): AttentionConcentration | null {
  if (broken.length < 2) return null;

  const groups = new Map<string, AttentionRow[]>();
  for (const row of broken) {
    // The step label, not the step id: an orphan has no step, and two orphans
    // on one workflow are the same story a reader would want grouped.
    //
    // NUL separates the two halves because it cannot occur in either, so no
    // workflow key and step label can collide by concatenating differently.
    // Written as the `\0` escape rather than the literal byte: a raw NUL makes
    // this file binary to grep and ripgrep, which then skip it silently — a
    // search for anything in here returns nothing rather than no match.
    const key = `${row.workflowKey}\0${row.stepLabel ?? ''}`;
    const bucket = groups.get(key) ?? [];
    bucket.push(row);
    groups.set(key, bucket);
  }

  let largest: AttentionRow[] = [];
  for (const bucket of groups.values()) {
    if (bucket.length > largest.length) largest = bucket;
  }

  const head = largest[0];
  if (!head || largest.length < 2) return null;
  if (largest.length * 2 <= broken.length) return null;

  const first = head.failureMessage;
  return {
    workflowKey: head.workflowKey,
    workflowName: head.workflowName,
    stepLabel: head.stepLabel,
    count: largest.length,
    total: broken.length,
    // Undefined counts as "no message", so a group of orphans — which carry
    // none — is not reported as sharing one.
    sameFailure:
      first !== undefined &&
      largest.every((row) => row.failureMessage === first),
    hasBoard: head.hasBoard,
  };
}

/**
 * Heaviest board first, where "heavy" means "most likely to need you".
 *
 * Strictly by severity rather than by a weighted total: one broken task
 * outranks any number of running ones, because running is the state that needs
 * nothing from anybody. A total would let five healthy running tasks push a
 * single broken one down the list, which is the one thing this ordering exists
 * to prevent.
 *
 * Name breaks the tie so the order is total and stable — boards that are all
 * equally quiet must not reshuffle between refreshes.
 */
function compareBoardLoad(a: BoardLoad, b: BoardLoad): number {
  return (
    b.broken - a.broken ||
    b.waiting - a.waiting ||
    b.running - a.running ||
    b.waitingOnSubtasks - a.waitingOnSubtasks ||
    b.total - a.total ||
    a.workflowName.localeCompare(b.workflowName)
  );
}

/** Place in line first; a row the service did not place goes last, then by title. */
function compareQueued(a: QueuedRow, b: QueuedRow): number {
  if (a.position !== b.position) {
    if (a.position === null) return 1;
    if (b.position === null) return -1;
    return a.position - b.position;
  }
  return a.title.localeCompare(b.title);
}

/** A parent's batch, one entry per subtask, in the board card's bar order. */
function segmentsOf(task: TaskView): LinkedTaskSummaryItemStatus[] {
  const statuses = (task.linked_tasks?.tasks ?? []).map((item) => item.status);
  return SEGMENT_ORDER.flatMap((status) =>
    statuses.filter((each) => each === status),
  );
}
